import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Form, ListGroup } from "react-bootstrap";

import Icon from "../Icon";
import {
    faArrowDown,
    faArrowUp,
    faCalendar,
    faCalendarCheck,
    faCalendarDay,
    faCalendarWeek,
    faClock,
    faPenToSquare,
    faPlus as add,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Duty, DutyPeriodicity } from "../../state/Duty";
import { emptyDuty } from "../../state/reducer";
import { useData } from "../../storage/accessors";

const PERIODICITY_LABELS: Record<DutyPeriodicity, string> = {
    EVERY_DAY: "Каждый день",
    WEEKDAYS: "По будням",
    WEEKENDS: "По выходным",
    WEEKLY: "Раз в неделю",
    MONTHLY: "Раз в месяц",
};

const PERIODICITY_ICONS: Record<DutyPeriodicity, any> = {
    EVERY_DAY: faCalendarDay,
    WEEKDAYS: faCalendarWeek,
    WEEKENDS: faCalendarCheck,
    WEEKLY: faCalendarWeek,
    MONTHLY: faCalendar,
};

type Props = {
    lifepartUuid: string;
    color: string;
};

export default function DutiesWidget({ lifepartUuid, color }: Props) {
    const { duties } = useData();
    // uuid обязанности в режиме редактирования; для новой — uuid ещё не сохранённого черновика
    const [editingUuid, setEditingUuid] = useState<string | null>(null);
    const [draft, setDraft] = useState<Duty | null>(null);

    const partDuties = useMemo(
        () =>
            Object.values(duties.objects)
                .filter((d) => d.lifepart === lifepartUuid)
                .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0) || (a.order ?? 0) - (b.order ?? 0)),
        [duties.objects, lifepartUuid]
    );

    const isNewDraft = !!draft && !duties.objects[draft.uuid];

    const startCreate = () => {
        // Новый элемент всегда идёт в конец списка — приоритет больше максимального
        const maxPriority = partDuties.reduce((max, d) => Math.max(max, d.priority ?? 0), -1);
        const created = emptyDuty(lifepartUuid, maxPriority + 1);
        setDraft(created);
        setEditingUuid(created.uuid);
    };

    const startEdit = (duty: Duty) => {
        setDraft({ ...duty });
        setEditingUuid(duty.uuid);
    };

    const cancel = () => {
        setDraft(null);
        setEditingUuid(null);
    };

    const save = () => {
        if (!draft || !draft.name.trim()) return;
        const { lastUpdateTimestamp, ...data } = draft;
        const payload = { ...data, name: draft.name.trim() };
        if (isNewDraft) {
            duties.create(payload as Duty);
        } else {
            duties.save(draft.uuid, payload);
        }
        cancel();
    };

    // Сдвиг обязанности в списке меняет её приоритет показа — меняем приоритеты местами с соседом
    const move = (duty: Duty, direction: -1 | 1) => {
        const index = partDuties.findIndex((d) => d.uuid === duty.uuid);
        const neighbor = partDuties[index + direction];
        if (!neighbor) return;
        duties.save(duty.uuid, { priority: neighbor.priority ?? 0 });
        duties.save(neighbor.uuid, { priority: duty.priority ?? 0 });
    };

    const renderForm = () =>
        draft && (
            <div className="d-flex flex-column gap-2">
                <Form.Control
                    type="text"
                    size="sm"
                    autoFocus
                    value={draft.name}
                    placeholder="Название обязанности"
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") save();
                        if (e.key === "Escape") cancel();
                    }}
                />
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <Form.Select
                        size="sm"
                        style={{ maxWidth: 190 }}
                        value={draft.periodicity}
                        onChange={(e) => setDraft({ ...draft, periodicity: e.target.value as DutyPeriodicity })}
                    >
                        {(Object.keys(PERIODICITY_LABELS) as DutyPeriodicity[]).map((key) => (
                            <option key={key} value={key}>
                                {PERIODICITY_LABELS[key]}
                            </option>
                        ))}
                    </Form.Select>
                    <div className="ms-auto d-flex gap-2">
                        <Button variant="primary" size="sm" onClick={save}>
                            Сохранить
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={cancel}>
                            Отменить
                        </Button>
                    </div>
                </div>
            </div>
        );

    const isEmpty = partDuties.length === 0 && !draft;

    return (
        <Card className="border-2 shadow-sm" style={{ borderColor: color }}>
            <Card.Header
                className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2"
                style={{ backgroundColor: `${color}14` }}
            >
                <div className="d-flex align-items-center gap-2">
                    <Icon icon={faClock} style={{ color }} />
                    <strong style={{ color }}>Обязанности</strong>
                    {partDuties.length > 0 && <Badge bg="secondary">{partDuties.length}</Badge>}
                </div>
            </Card.Header>

            <Card.Body>
                {isEmpty ? (
                    <div className="text-center py-4">
                        <div className="text-muted mb-2">
                            <Icon icon={faClock} style={{ fontSize: "1.5rem", opacity: 0.4 }} />
                        </div>
                        <p className="text-muted mb-3">
                            <strong>Обязанности</strong> — это регулярные действия роли, которые повторяются изо дня в день
                            <br />
                            Например: зарядка, чтение, проверка почты.
                        </p>
                        <Button variant="primary" size="sm" onClick={startCreate}>
                            <Icon icon={add} className="me-1" /> Добавить первую обязанность
                        </Button>
                    </div>
                ) : (
                    <>
                        <ListGroup variant="flush">
                            {partDuties.map((duty, index) => {
                                if (editingUuid === duty.uuid) {
                                    return (
                                        <ListGroup.Item key={duty.uuid} className="px-2 py-3 border-0 bg-primary-subtle" style={{ borderRadius: 4 }}>
                                            {renderForm()}
                                        </ListGroup.Item>
                                    );
                                }

                                return (
                                    <ListGroup.Item
                                        key={duty.uuid}
                                        className="px-2 py-2 border-0"
                                        style={{
                                            borderLeft: `4px solid ${color} !important`,
                                            marginBottom: 4,
                                            borderRadius: 4,
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="flex-grow-1">
                                                <span>{duty.name || "(без названия)"}</span>
                                                <div className="mt-1">
                                                    <Badge bg="light" className="fw-normal text-dark">
                                                        <Icon icon={PERIODICITY_ICONS[duty.periodicity]} className="me-1" />
                                                        {PERIODICITY_LABELS[duty.periodicity]}
                                                    </Badge>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1 flex-shrink-0">
                                                <Button variant="outline-secondary" size="sm" disabled={index === 0} onClick={() => move(duty, -1)}>
                                                    <Icon icon={faArrowUp} />
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    disabled={index === partDuties.length - 1}
                                                    onClick={() => move(duty, 1)}
                                                >
                                                    <Icon icon={faArrowDown} />
                                                </Button>
                                                <Button variant="outline-primary" size="sm" onClick={() => startEdit(duty)}>
                                                    <Icon icon={faPenToSquare} />
                                                </Button>
                                                <Button variant="outline-danger" size="sm" onClick={() => duties.delete(duty.uuid)}>
                                                    <Icon icon={faTrash} />
                                                </Button>
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                );
                            })}

                            {isNewDraft && (
                                <ListGroup.Item className="px-2 py-3 border-0 bg-primary-subtle" style={{ borderRadius: 4 }}>
                                    {renderForm()}
                                </ListGroup.Item>
                            )}
                        </ListGroup>

                        {!editingUuid && partDuties.length > 0 && (
                            <div className="mt-3">
                                <Button variant="outline-success" size="sm" onClick={startCreate}>
                                    <Icon icon={add} className="me-1" /> Добавить обязанность
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Card.Body>
        </Card>
    );
}